import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Grid } from '@simplywallst/ui-core';
import {
  IntroductionWrapper,
  SectionDescription,
  SnowflakeWrapper,
  Score,
  ContentWrapper,
  MobileTitleWrapper,
  SnowflakeFallback,
} from './styled';
import { SectionTitle } from '@/components/ReportSectionHeader';
import StatementChecks from './components/StatementChecks';
import { useSummaryV2 } from '@/hooks/useCompany';
import loadable from '@loadable/component';
import type { SnowflakeSection } from '@/components/DeprecatedSnowflake/SnowflakeCanvas';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import { SnowflakeType } from '@/components/DeprecatedSnowflake/SnowflakeType';
import { shouldSkipDefer } from '@/redux/ui/selectors/shouldSkipDefer';
import { shallowEqual, useSelector } from 'react-redux';
import type { CompanyReportSection } from '@/pages/CompanyReport/useSection';
import { useDwellTracking } from '@/pages/CompanyReport/components/DwellTrackingProvider';
import type { StatementChecks as Checks } from '@/pages/CompanyReport/components/Introduction/utils';
import { useTranslation } from '@/hooks/useTranslation';

const SnowflakeMobile = loadable(
  () => import('@/components/DeprecatedSnowflake'),
  {
    ssr: false,
    fallback: <SnowflakeFallback />,
  }
);

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'), {
  ssr: false,
});

const snowflakeSections = ['dividend', 'future', 'health', 'past', 'value'];
export const mapReportSectionToSnowflakeSection = (
  section: CompanyReportSection
): SnowflakeSection | undefined => {
  if (section === 'valuation') return 'value';
  if (snowflakeSections.includes(section)) return section as SnowflakeSection;
};

interface Props {
  title: ReactNode;
  description?: string;
  checks: Checks[];
  section: CompanyReportSection;
  children?: ReactNode;
}
export const IntroductionDeprecated = ({
  title,
  description,
  checks,
  section,
  children,
}: Props) => {
  const summary = useSummaryV2();
  const { observe } = useDwellTracking();
  const snowflakeSection = mapReportSectionToSnowflakeSection(section);
  const skipDefer = useSelector<
    Parameters<typeof shouldSkipDefer>[0],
    ReturnType<typeof shouldSkipDefer>
  >(shouldSkipDefer, shallowEqual);
  const totalScore = useMemo(() => {
    return checks.filter((current) => current.mark === 'PASS').length;
  }, [checks]);

  const t = useTranslation();

  return (
    <IntroductionWrapper
      data-cy-id="Valuation-introduction"
      data-section={section}
      ref={observe}
    >
      {typeof title === 'string' ? <SectionTitle>{title}</SectionTitle> : title}
      {description && <SectionDescription>{description}</SectionDescription>}
      <Grid gap="0px" template="1fr / 1fr" templateSm="1fr / repeat(2, 1fr)">
        <ContentWrapper>
          <Grid template="2fr / 1fr">
            <MobileTitleWrapper>
              <Grid template="1fr / repeat(2, 1fr)" alignItems="center">
                <Score data-cy-id="valuation-scores">
                  <p>{t('value.scoreTitle')}</p>
                  <span>{` ${totalScore}/${checks.length}`}</span>
                </Score>
                {summary.status === 'success' ? (
                  <DeferRenderUntilIntersecting
                    skipDefer={skipDefer}
                    fallback={() => <SnowflakeFallback />}
                  >
                    {() => (
                      <SnowflakeMobile
                        points={summary.score}
                        tooltip={false}
                        type={SnowflakeType.TOC}
                        highlightSection={snowflakeSection}
                      />
                    )}
                  </DeferRenderUntilIntersecting>
                ) : (
                  <SnowflakeFallback />
                )}
              </Grid>
              <hr />
            </MobileTitleWrapper>
            <Grid>
              <StatementChecks
                title={t('value.scoreTitle')}
                testId="valuation-score"
                checks={checks}
                score={totalScore}
              />
            </Grid>
          </Grid>
        </ContentWrapper>
        <SnowflakeWrapper>
          {summary.status === 'success' ? (
            <DeferRenderUntilIntersecting
              preRenderHeight={248}
              preRenderHeightMdDown={248}
              preRenderHeightSmDown={248}
              skipDefer={skipDefer}
            >
              {() => (
                <Snowflake
                  points={summary.score}
                  tooltip={false}
                  type={SnowflakeType.TOC}
                  highlightSection={snowflakeSection}
                  width={200}
                />
              )}
            </DeferRenderUntilIntersecting>
          ) : null}
        </SnowflakeWrapper>
      </Grid>
      {children}
    </IntroductionWrapper>
  );
};
